import { Dialog, DialogContent } from '@features/ui';
import { paymentFormSchema, PaymentFormValues } from '@src/types/packages';
import { PurchaseResetPackage } from '@src/types/purchaseReset';
import { useAuthStore } from '@stores/authStore';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { BillingForm } from '@src/features/user/checkout';

type CheckoutDialogAddon = {
  title: string;
  price: number;
  monthlyPrice?: number;
};

type CheckoutDialogAddonGroup = {
  title: string;
  addons: CheckoutDialogAddon[];
};

interface CheckoutDialogProps {
  open: boolean;
  package: PurchaseResetPackage | null;
  loading?: boolean;
  submitting?: boolean;
  title: string;
  description?: string;
  packageDisplayName?: string;
  /** Flat list of addons (no grouping). */
  addons?: CheckoutDialogAddon[];
  /** Grouped addons: each group has a title and addons inside. */
  addonGroups?: CheckoutDialogAddonGroup[];
  /**
   * If true, addons are treated as monthly subscription items.
   * When true and addonMonthlyUnitPrice is provided (default $5), each addon displays that price.
   */
  isAddonMonthlySubscription?: boolean;
  addonMonthlyUnitPrice?: number;
  showMonthlyBreakdown?: boolean;
  productMonthlyPrice?: number;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: PaymentFormValues) => Promise<void> | void;
}

export const CheckoutDialog: FC<CheckoutDialogProps> = ({
  open,
  package: packageData,
  loading,
  submitting,
  title,
  description = 'Review your order details and confirm your billing information',
  packageDisplayName,
  addons,
  addonGroups,
  isAddonMonthlySubscription,
  addonMonthlyUnitPrice = 5,
  showMonthlyBreakdown = false,
  productMonthlyPrice = 0,
  onOpenChange,
  onSubmit,
}) => {
  const { auth } = useAuthStore();

  const packagePrice = useMemo(() => {
    if (!packageData?.billing_plan?.first_price) return 0;

    return packageData.price ?? packageData.billing_plan?.first_price ?? 0;
  }, [packageData]);

  const displayName = packageDisplayName || title;

  const resolvedAddonGroups = useMemo((): CheckoutDialogAddonGroup[] => {
    if (addonGroups?.length) return addonGroups;

    if (addons?.length) {
      return [
        {
          title: 'Add Ons',
          addons,
        },
      ];
    }

    return [];
  }, [addonGroups, addons]);

  const addonsTotal = useMemo(() => {
    if (!resolvedAddonGroups.length) return 0;

    return resolvedAddonGroups.reduce(
      (sum, g) => sum + g.addons.reduce((s2, a) => s2 + (a?.price ?? 0), 0),
      0
    );
  }, [resolvedAddonGroups]);

  const addonsMonthlyTotal = useMemo(() => {
    if (!resolvedAddonGroups.length || !isAddonMonthlySubscription) return 0;

    return resolvedAddonGroups.reduce(
      (sum, g) =>
        sum +
        g.addons.reduce(
          (s2, a) => s2 + (a?.monthlyPrice ?? addonMonthlyUnitPrice),
          0
        ),
      0
    );
  }, [
    addonMonthlyUnitPrice,
    isAddonMonthlySubscription,
    resolvedAddonGroups,
  ]);

  const totalMonthlyWithProduct = useMemo(() => {
    if (!isAddonMonthlySubscription) return 0;

    return addonsMonthlyTotal + productMonthlyPrice;
  }, [addonsMonthlyTotal, isAddonMonthlySubscription, productMonthlyPrice]);

  const totalDueToday = useMemo(() => {
    return packagePrice + addonsTotal;
  }, [addonsTotal, packagePrice]);

  const defaultValues: Partial<PaymentFormValues> = useMemo(
    () => ({
      payment_type: 'stripe',
      first_name: auth.user?.first_name ?? '',
      last_name: auth.user?.last_name ?? '',
      street: '',
      city: auth.user?.city ?? '',
      state: auth.user?.state ?? '',
      zip_code: auth.user?.zipcode ?? '',
      country: auth.user?.country ?? 'US',
      phone_number: auth.user?.phone_number ?? '',
      coupon_code: '',
      referral_source: '',
    }),
    [auth.user]
  );

  const form = useForm<PaymentFormValues>({
    resolver: yupResolver(paymentFormSchema),
    defaultValues,
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [defaultValues, open, reset]);

  const onSubmitForm = async (values: PaymentFormValues) => {
    await onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-[80vw] max-h-[90vh] overflow-y-auto"
        size="unset"
      >
        {loading ? (
          <div className="py-20 text-center">
            <p className="text-gray-400 text-lg">Loading purchase details…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmitForm)}>
            <div className="bg-[#1F2734] rounded-[10px] flex flex-col lg:flex-row gap-6 p-6">
              {/* Left Side - Order Summary */}
              <div className="flex-1 space-y-4">
                <div>
                  <h2 className="font-bold text-[28px] leading-[100%] tracking-normal text-white mb-2">
                    {title}
                  </h2>
                  <p className="pt-[10px] font-medium text-[16px] leading-[18px] tracking-normal text-white/70 max-w-[617px]">
                    {description}
                  </p>
                </div>

                <div className="rounded-[10px] p-[20px] flex flex-col gap-[30px]">
                  <div className="flex flex-col items-start justify-center p-[30px] pt-5 bg-[#253348] rounded-[10px] border-[#33455F] border">
                    <p className="text-[18px] font-bold text-white mb-4">
                      Order Summary
                    </p>

                    <div className="flex items-center justify-between w-full gap-[15px]">
                      <p className={'text-white font-bold text-base'}>
                        {displayName}
                      </p>
                      <p className={'text-white font-bold text-base'}>
                        ${packagePrice.toFixed(2)}
                      </p>
                    </div>

                    {resolvedAddonGroups.length ? (
                      <div className={'mt-[30px] w-full space-y-[18px]'}>
                        {resolvedAddonGroups.map((group, groupIdx) => (
                          <div key={`${group.title}_${groupIdx}`}>
                            <p className={'text-sm text-[#818FA4] font-normal'}>
                              {group.title}
                            </p>
                            <div className={'mt-[10px] space-y-[6px]'}>
                              {group.addons.map((a, idx) => {
                                const addonPrice = isAddonMonthlySubscription
                                  ? (a.monthlyPrice ?? addonMonthlyUnitPrice)
                                  : a.price;

                                return (
                                  <div
                                    key={`${groupIdx}_${a.title}_${idx}`}
                                    className={
                                      'flex items-center justify-between w-full gap-[6px] ps-[10px]'
                                    }
                                  >
                                    <p
                                      className={
                                        'text-white font-normal text-[14px]'
                                      }
                                    >
                                      {a.title}
                                    </p>
                                    <p
                                      className={
                                        'text-[#818FA4] font-normal text-sm'
                                      }
                                    >
                                      ${addonPrice.toFixed(2)}
                                      {isAddonMonthlySubscription ? '/mo' : ''}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}

                        {showMonthlyBreakdown && isAddonMonthlySubscription ? (
                          <>
                            <div
                              className={
                                'flex items-center justify-between w-full gap-[6px] ps-[10px]'
                              }
                            >
                              <p className={'text-white font-bold text-[14px]'}>
                                Total Next Monthly Billing
                              </p>
                              <p
                                className={
                                  'text-[#818FA4] font-normal text-[16px] text-bold text-[#FFFFFF]'
                                }
                              >
                                ${addonsMonthlyTotal.toFixed(2)}
                              </p>
                            </div>
                            <div
                              className={
                                'flex items-center justify-between w-full gap-[6px] ps-[10px]'
                              }
                            >
                              <p className={'text-white font-bold text-[14px]'}>
                                Total
                              </p>
                              <p
                                className={
                                  'text-[#818FA4] font-normal text-[16px] text-bold text-[#FFFFFF]'
                                }
                              >
                                ${addonsTotal.toFixed(2)}
                              </p>
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}

                    {/* Total Divider */}
                    <div className="border-b-[3px] w-full border-[#33455F] pt-[30px] mb-[30px]" />

                    {showMonthlyBreakdown && isAddonMonthlySubscription ? (
                      <div className="flex items-center justify-between w-full gap-[6px]">
                        <p className={'text-white font-bold text-[14px]'}>
                          Total (Monthly)
                        </p>
                        <p className={'text-white font-normal text-sm'}>
                          ${totalMonthlyWithProduct.toFixed(2)}
                        </p>
                      </div>
                    ) : null}

                    {/* Total Due Today */}
                    <div className="flex items-center justify-between w-full mt-5 gap-[6px]">
                      <p className="text-white font-bold text-[22px]">
                        Total Due Today
                      </p>
                      <p className="text-white font-normal text-[22px]">
                        ${totalDueToday.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Billing Information */}
              <div className="flex flex-1 gap-10">
                <BillingForm
                  form={form}
                  showSubmitButton={true}
                  submitButtonText="Checkout"
                  submitting={submitting}
                />
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
